import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Plus, GripVertical, Archive, ChevronUp, ChevronDown, Sparkles, Trash2, Info, ExternalLink, Users as UsersIcon } from "lucide-react";
import Cloud from "lucide-react/dist/esm/icons/cloud";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  PageHeader, Card, Field, Input, NumberInput,
  PrimaryButton, QuietButton
} from "../components/UI";
import { useAuth } from "../contexts/AuthContext";
import { UsersTab } from "./settings/UsersTab";
import { MilestonesTab } from "./settings/MilestonesTab";
import { JobTypesTab } from "./settings/JobTypesTab";
import {
  WorkingCalendarTab, ProgrammeDefaultsTab, ExportDefaultsTab, ProvsumDefaultsTab,
  InsuranceTab, DocumentCopyTab,
} from "./settings/CompanyTabs";
import { useJobTypeLibrary } from "../hooks/useJobTypeLibrary";
import { useSearchParams, Link } from "react-router-dom";
import { LabourRatesBulkTab } from "./settings/LabourRatesBulkTab";
import MaterialsSettingsTab from "./MaterialsSettingsTab";
import { BluebeamSetupSteps } from "../components/BluebeamOpen";

/* Library settings live in two places on purpose — under the library itself
 * (Libraries → Materials → Settings) and here under one "Library settings"
 * drop-down — so the same panel is reachable from either route. */
function LibraryTabButton({ label, items, active, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}
        data-testid="settings-tab-library"
        className={["relative py-3 px-3 transition-colors inline-flex items-center gap-1", active ? "text-ink" : "text-ink-3 hover:text-ink-2"].join(" ")}
        style={{ fontSize: 13, fontWeight: active ? 600 : 500 }}>
        {label}{active ? <span className="text-ink-3" style={{ fontWeight: 500 }}> · {active.label}</span> : null}
        <ChevronDown size={12} className={["transition-transform", open ? "rotate-180" : ""].join(" ")} />
        {active && (<span className="absolute left-3 right-3 -bottom-px h-[2px]" style={{ background: "#2F4F3E" }} />)}
      </button>
      {open && (
        <div role="menu" className="absolute left-0 top-full mt-1 z-40 min-w-[260px] rounded-md border border-line bg-surface shadow-card py-1" data-testid="settings-library-menu">
          {items.map((it) => (
            <button key={it.id} type="button" role="menuitem" onClick={() => { onPick(it.id); setOpen(false); }}
              data-testid={`settings-library-menu-${it.id}`}
              className={["w-full text-left px-3 py-2 hover:bg-surface-2", active?.id === it.id ? "bg-surface-2" : ""].join(" ")}>
              <div className="text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>{it.label}</div>
              <div className="text-ink-4" style={{ fontSize: 11 }}>{it.hint}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LibraryCrossLink({ to, label }) {
  return (
    <div className="mb-3 text-ink-4" style={{ fontSize: 11.5 }} data-testid="settings-library-crosslink">
      Also under <Link to={to} className="underline hover:text-ink">{label}</Link> — edit from either place.
    </div>
  );
}

const LIBRARY_TABS = [
  { id: "library_materials", label: "Materials", hint: "Categories · units · suppliers · import", to: "/materials?tab=settings" },
  { id: "library_labour",    label: "Labour Rates", hint: "Update rates across the board", to: "/labour-rates?tab=settings" },
];
const LEGACY_TAB_IDS = { labour_rates: "library_labour" };

const TABS = [
  { id: "general",   label: "General" },
  { id: "users",     label: "Users", adminOnly: true },
  { id: "library",   label: "Library settings", group: LIBRARY_TABS },
  { id: "job_types", label: "Job Types" },
  { id: "phases",    label: "Master Phase List" },
  { id: "milestones", label: "Milestones" },
  { id: "working_calendar", label: "Working Calendar" },
  { id: "programme", label: "Programme" },
  { id: "export_defaults", label: "Export Defaults" },
  { id: "provsum_defaults", label: "Prov-Sum Defaults" },
  { id: "insurance", label: "Insurance" },
  { id: "dictionary", label: "Custom Dictionary" },
  // "Tender Checks" tab retired — its Day Rate Check constants now live
  // under Benchmarks & Defaults alongside prelims band, £/m², programme
  // and Insights thresholds. Route `/settings/tender-checks-config`
  // still resolves for a release cycle so bookmarks don't 404.
  { id: "benchmarks", label: "Benchmarks & Defaults" },
  { id: "output",    label: "Output Templates" },
  { id: "ai",        label: "AI Prompts" },
  { id: "integrations", label: "Integrations" },
  { id: "connectors", label: "Connectors" }
];

// LT-16 Part 2d — brand enable/disable toggles.
function BrandsToggles() {
  const [enabled, setEnabled] = useState({ lux_construction: false, lux_developments: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/brands")
      .then((r) => setEnabled(r.data?.brands_enabled || enabled))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const flip = async (brand) => {
    const next = { ...enabled, [brand]: !enabled[brand] };
    if (!next.lux_construction && !next.lux_developments) {
      toast.error("At least one brand must remain enabled.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/settings/brands", { brands_enabled: next });
      setEnabled(r.data?.brands_enabled || next);
      toast.success("Brands updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  if (loading) return <div className="text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;
  return (
    <div className="flex flex-col gap-2 max-w-md" data-testid="brands-toggles">
      {[["lux_developments", "Lux Developments"], ["lux_construction", "Lux Construction"]].map(([k, label]) => (
        <label key={k} className="flex items-center justify-between gap-3 p-3 rounded-md border border-line bg-surface" data-testid={`brand-toggle-${k}`}>
          <div className="flex flex-col">
            <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
            <span className="text-ink-3" style={{ fontSize: 11.5 }}>
              {enabled[k] ? "Enabled — appears in pickers" : "Disabled — hidden from pickers"}
            </span>
          </div>
          <input
            type="checkbox"
            checked={!!enabled[k]}
            onChange={() => flip(k)}
            disabled={saving}
            className="h-4 w-4"
            data-testid={`brand-toggle-input-${k}`}
          />
        </label>
      ))}
    </div>
  );
}

// LT-15 — the hardcoded JOB_TYPES const that used to live here is gone.
// Every chip site now reads from `useJobTypeLibrary()` so a rename in
// Settings → Job Types propagates without a redeploy. Archived types
// stay resolvable for tenders that already use them (see
// hooks/useJobTypeLibrary for the exact resolution semantics).

function GeneralTab() {
  const { user, setUser } = useAuth();
  const [wd, setWd] = useState({ hours_per_day: 8.0, days_per_week: 5, working_hours_from: "08:00", working_hours_to: "17:00" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/working-day").then((r) => setWd(r.data || wd))
       .catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/working-day", wd); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const setBrand = async (b) => {
    try { const r = await api.patch("/auth/me", { preferred_brand: b }); setUser(r.data); toast.success("Brand updated"); }
    catch { toast.error("Failed to update brand"); }
  };

  if (loading) return <div className="text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;

  return (
    <div className="flex flex-col gap-6">
      <Card className="p-5">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>Working Day</h3>
        <p className="text-ink-3 mb-4" style={{ fontSize: 12 }}>
          Used to convert between hourly and daily labour rates everywhere in the app.
        </p>
        <div className="grid grid-cols-3 gap-4 max-w-2xl">
          <Field label="Hours per day">
            <NumberInput value={wd.hours_per_day} step="0.5" onChange={(v) => setWd((s) => ({ ...s, hours_per_day: Number(v || 0) }))} suffix="h" />
          </Field>
          <Field label="Days per week">
            <NumberInput value={wd.days_per_week} step="1" onChange={(v) => setWd((s) => ({ ...s, days_per_week: Number(v || 0) }))} />
          </Field>
          <div />
          <Field label="Hours from" hint="Informational">
            <Input value={wd.working_hours_from} onChange={(v) => setWd((s) => ({ ...s, working_hours_from: v }))} placeholder="08:00" mono />
          </Field>
          <Field label="Hours to" hint="Informational">
            <Input value={wd.working_hours_to} onChange={(v) => setWd((s) => ({ ...s, working_hours_to: v }))} placeholder="17:00" mono />
          </Field>
        </div>
        <div className="mt-5 flex justify-end">
          <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : Save}>Save</PrimaryButton>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>Brands</h3>
        <p className="text-ink-3 mb-4 max-w-2xl" style={{ fontSize: 12 }}>
          LUX Developments is the primary trading brand — leave the other off unless you're
          actively using both. A disabled brand disappears from every picker in the app, but
          tenders already using it keep displaying it. At least one brand must be enabled.
        </p>
        <BrandsToggles />
      </Card>

      <Card className="p-5">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>Brand preference</h3>
        <p className="text-ink-3 mb-4" style={{ fontSize: 12 }}>Your default brand for new tenders.</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setBrand("lux_construction")}
            className={["h-9 px-4 rounded-md border transition-colors", user?.preferred_brand === "lux_construction"
              ? "bg-accent-soft border-accent text-accent font-medium"
              : "border-line bg-surface text-ink-2 hover:bg-surface-2"].join(" ")}
            style={{ fontSize: 13 }}
          >Lux Construction</button>
          <button
            onClick={() => setBrand("lux_developments")}
            className={["h-9 px-4 rounded-md border transition-colors", user?.preferred_brand === "lux_developments"
              ? "bg-accent-soft border-accent text-accent font-medium"
              : "border-line bg-surface text-ink-2 hover:bg-surface-2"].join(" ")}
            style={{ fontSize: 13 }}
          >Lux Developments</button>
        </div>
      </Card>

      {/* LT-Estimate-UX-01 follow-up — Labour Rates shortcut. Labour rates
          live under Libraries → Labour Rates (they're not workspace
          settings; they're a library because they're consumed across
          every tender AND every library recipe). This card exists to
          answer "where do I set trade rates?" in the place users
          naturally look.
          LT-17 §6 — Labour Rates AND Material Rates are also now in
          the top Libraries menu, so this card is a courtesy pointer
          rather than the only way in. Same treatment applied to A&O
          and Materials so no signposting text sends users to a
          menu that doesn't have the item. */}
      <Card className="p-5" data-testid="settings-labour-rates-shortcut">
        <h3 className="font-display text-ink mb-1 inline-flex items-center gap-2" style={{ fontSize: 14, fontWeight: 600 }}>
          <UsersIcon size={14} className="text-ink-3" />
          Labour rates
        </h3>
        <p className="text-ink-3 mb-4" style={{ fontSize: 12 }}>
          Trade rates (Bricklayer, Carpenter, Plumber, etc.) live under Libraries → Labour Rates
          because they're consumed by every tender and every library recipe, not just workspace
          config. Each trade holds a <strong>Charge rate</strong> (billed to the client) and a
          <strong> Pay rate</strong> (cost to LUX) — the difference feeds the internal Labour Buffer
          on the tender Summary tab.
        </p>
        <a
          href="/labour-rates"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-accent text-white hover:bg-accent-deep transition-colors"
          style={{ fontSize: 13, fontWeight: 500 }}
          data-testid="settings-labour-rates-link"
        >
          Manage Labour Rates
          <ExternalLink size={12} />
        </a>
      </Card>
    </div>
  );
}

function PhasesTab() {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState("");
  const [editingId, setEditingId] = useState(null); // which phase's picker is open
  // LT-Estimate-UX-01 follow-up — Bulk phase tagging dialog. Instead of
  // opening each phase's picker in turn, tag many phases with the same
  // job-type set in one action.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPickedPhases, setBulkPickedPhases] = useState(new Set());
  const [bulkPickedJobTypes, setBulkPickedJobTypes] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  // LT-15 — chips come from the library, not a hardcoded const. Active
  // types drive the picker; archived types are still resolved for
  // label display via `labelFor`.
  const { activeTypes: jobTypes, labelFor: jobTypeLabelFor } = useJobTypeLibrary();

  const load = () =>
    api.get("/settings/master-phases")
      .then((r) => setPhases(r.data || []))
      .catch(() => toast.error("Failed to load phases"))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const move = async (idx, dir) => {
    const next = [...phases];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setPhases(next);
    try { await api.put("/settings/master-phases/reorder", { order: next.map((p) => p.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this phase?")) return;
    try { await api.delete(`/settings/master-phases/${id}`); toast.success("Phase archived"); load(); }
    catch { toast.error("Failed"); }
  };

  const add = async () => {
    if (!adding.trim()) return;
    try { await api.post("/settings/master-phases", { name: adding.trim() }); setAdding(""); toast.success("Phase added"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Save the job-type list for one phase. Empty list = "applies to all"
  // (matches the A&O convention). Persists optimistically.
  const saveJobTypes = async (phase, nextKeys) => {
    setPhases((prev) => prev.map((p) => (p.id === phase.id ? { ...p, applies_to_job_types: nextKeys } : p)));
    try {
      await api.patch(`/settings/master-phases/${phase.id}`, { applies_to_job_types: nextKeys });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save job types");
      load();
    }
  };

  if (loading) return <div className="text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;

  // LT-Estimate-UX-01 follow-up — apply the currently-picked job types
  // to every currently-picked phase. Empty job-type set = revert those
  // phases to "all job types" (empty array on the model). Fires N
  // PATCH calls in parallel; on any failure we fully reload from server.
  const applyBulk = async () => {
    if (bulkPickedPhases.size === 0) {
      toast.error("Pick at least one phase");
      return;
    }
    setBulkSaving(true);
    const nextTags = Array.from(bulkPickedJobTypes);
    const ids = Array.from(bulkPickedPhases);
    try {
      await Promise.all(ids.map((pid) =>
        api.patch(`/settings/master-phases/${pid}`, { applies_to_job_types: nextTags })
      ));
      toast.success(
        `Tagged ${ids.length} phase${ids.length === 1 ? "" : "s"} ` +
        (nextTags.length === 0 ? "as applying to all job types" : `with ${nextTags.length} job type${nextTags.length === 1 ? "" : "s"}`)
      );
      setBulkOpen(false);
      setBulkPickedPhases(new Set());
      setBulkPickedJobTypes(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk tag failed");
      load();
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>Master Phase List</h3>
          <p className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
            These are the phases that appear when building a tender. Reorder by clicking the arrows.
            Tag a phase with specific job types (e.g. omit Substructure from Loft Conversion) — an empty tag list means the phase applies to every job type.
          </p>
        </div>
        <QuietButton
          onClick={() => { setBulkOpen(true); setBulkPickedPhases(new Set()); setBulkPickedJobTypes(new Set()); }}
          icon={Sparkles}
          data-testid="phases-bulk-tag-btn"
        >
          Bulk tag
        </QuietButton>
      </div>
      {bulkOpen && (
        <div
          className="rounded-md border border-accent/30 bg-accent-soft/20 mb-4"
          data-testid="phases-bulk-tag-dialog"
        >
          <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-line-soft">
            <div className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
              Bulk tag phases with job types
            </div>
            <button
              onClick={() => setBulkOpen(false)}
              className="text-ink-3 hover:text-ink"
              style={{ fontSize: 11 }}
              data-testid="phases-bulk-tag-cancel"
            >
              Cancel
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 p-4">
            {/* Left — phase checkbox list */}
            <div>
              <div className="col-head mb-2">Phases ({bulkPickedPhases.size} selected)</div>
              <div className="flex items-center gap-3 mb-2" style={{ fontSize: 11 }}>
                <button
                  className="text-accent hover:underline"
                  onClick={() => setBulkPickedPhases(new Set(phases.map((p) => p.id)))}
                  data-testid="phases-bulk-select-all"
                >Select all</button>
                <span className="text-ink-4">·</span>
                <button
                  className="text-accent hover:underline"
                  onClick={() => setBulkPickedPhases(new Set())}
                  data-testid="phases-bulk-select-none"
                >Select none</button>
              </div>
              <div className="max-h-72 overflow-y-auto scrollbar-thin border border-line-soft rounded-md bg-surface">
                {phases.map((p) => {
                  const checked = bulkPickedPhases.has(p.id);
                  return (
                    <label
                      key={p.id}
                      className={cls(
                        "flex items-center gap-2 px-3 py-1.5 border-b border-line-soft last:border-0 cursor-pointer",
                        checked ? "bg-accent/5" : "hover:bg-surface-2"
                      )}
                      style={{ fontSize: 12.5 }}
                      data-testid={`phases-bulk-check-${p.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setBulkPickedPhases((prev) => {
                            const nx = new Set(prev);
                            if (e.target.checked) nx.add(p.id); else nx.delete(p.id);
                            return nx;
                          });
                        }}
                      />
                      <span className="font-mono tnum text-ink-3" style={{ fontSize: 11 }}>{String(p.default_sequence).padStart(2, "0")}</span>
                      <span className="text-ink">{p.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            {/* Right — job type chip picker */}
            <div>
              <div className="col-head mb-2">Apply job types ({bulkPickedJobTypes.size === 0 ? "All job types" : `${bulkPickedJobTypes.size} selected`})</div>
              <p className="text-ink-3 mb-2" style={{ fontSize: 11 }}>
                Leave empty to mark the selected phases as applying to all job types.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setBulkPickedJobTypes(new Set())}
                  className={cls(
                    "rounded-full px-2 py-0.5 border transition-colors",
                    bulkPickedJobTypes.size === 0
                      ? "border-accent bg-accent text-white"
                      : "border-line text-ink-2 hover:bg-surface-2"
                  )}
                  style={{ fontSize: 11 }}
                  data-testid="phases-bulk-jt-all"
                >
                  All job types
                </button>
                {jobTypes.map((jt) => {
                  const active = bulkPickedJobTypes.has(jt.key);
                  return (
                    <button
                      key={jt.key}
                      type="button"
                      onClick={() => {
                        setBulkPickedJobTypes((prev) => {
                          const nx = new Set(prev);
                          if (nx.has(jt.key)) nx.delete(jt.key); else nx.add(jt.key);
                          return nx;
                        });
                      }}
                      className={cls(
                        "rounded-full px-2 py-0.5 border transition-colors",
                        active
                          ? "border-accent bg-accent text-white"
                          : "border-line text-ink-2 hover:bg-surface-2"
                      )}
                      style={{ fontSize: 11 }}
                      data-testid={`phases-bulk-jt-${jt.key}`}
                    >
                      {jt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="px-4 py-3 border-t border-line-soft flex items-center justify-end">
            <PrimaryButton
              onClick={applyBulk}
              disabled={bulkSaving || bulkPickedPhases.size === 0}
              icon={bulkSaving ? Loader2 : Save}
              data-testid="phases-bulk-apply"
            >
              {bulkSaving ? "Applying…" : "Apply tags"}
            </PrimaryButton>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-1.5 mb-4">
        {phases.map((p, i) => {
          const tags = p.applies_to_job_types || [];
          const isAll = tags.length === 0 || tags.map((s) => s.toLowerCase()).includes("all");
          const isPickerOpen = editingId === p.id;
          return (
            <div key={p.id}
              className="rounded-md border border-line bg-surface"
              data-testid={`master-phase-row-${p.id}`}
            >
              <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <GripVertical size={13} className="text-ink-4 flex-shrink-0" />
                  <span className="font-mono tnum text-ink-3 flex-shrink-0" style={{ fontSize: 11 }}>{String(p.default_sequence).padStart(2, "0")}</span>
                  <span className="text-ink flex-shrink-0" style={{ fontSize: 13 }}>{p.name}</span>
                  <div className="flex items-center gap-1 flex-wrap ml-2">
                    {isAll ? (
                      <span
                        className="rounded-full px-2 py-0.5 border border-line-soft text-ink-3 bg-surface-2/60"
                        style={{ fontSize: 10.5, letterSpacing: "0.04em", textTransform: "uppercase" }}
                      >
                        All job types
                      </span>
                    ) : (
                      tags.map((k) => (
                        <span key={k}
                          className="rounded-full px-2 py-0.5 border border-accent/30 text-accent bg-accent/5"
                          style={{ fontSize: 10.5, letterSpacing: "0.04em", textTransform: "uppercase" }}
                        >
                          {jobTypeLabelFor(k)}
                        </span>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => setEditingId(isPickerOpen ? null : p.id)}
                    className="h-7 px-2 rounded text-ink-3 hover:text-ink hover:bg-surface-2 inline-flex items-center gap-1"
                    title="Edit job types"
                    data-testid={`master-phase-edit-jt-${p.id}`}
                    style={{ fontSize: 11 }}
                  >
                    {isPickerOpen ? "Done" : "Job types"}
                  </button>
                  <button onClick={() => move(i, -1)} className="h-7 w-7 rounded text-ink-3 hover:bg-surface-2 hover:text-ink"><ChevronUp size={13} className="mx-auto" /></button>
                  <button onClick={() => move(i, 1)}  className="h-7 w-7 rounded text-ink-3 hover:bg-surface-2 hover:text-ink"><ChevronDown size={13} className="mx-auto" /></button>
                  <button onClick={() => archive(p.id)} className="h-7 px-2 rounded text-ink-3 hover:text-note-red hover:bg-surface-2" title="Archive"><Archive size={12} /></button>
                </div>
              </div>
              {isPickerOpen && (
                <div
                  className="border-t border-line-soft px-3 py-2.5 bg-surface-2/30 flex items-center gap-2 flex-wrap"
                  data-testid={`master-phase-jt-picker-${p.id}`}
                >
                  <span className="text-ink-3 mr-2" style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase" }}>Applies to:</span>
                  <button
                    type="button"
                    onClick={() => saveJobTypes(p, [])}
                    className={cls(
                      "rounded-full px-2 py-0.5 border transition-colors",
                      isAll
                        ? "border-accent bg-accent text-white"
                        : "border-line text-ink-2 hover:bg-surface-2"
                    )}
                    style={{ fontSize: 11 }}
                    data-testid={`master-phase-jt-all-${p.id}`}
                  >
                    All job types
                  </button>
                  {jobTypes.map((jt) => {
                    const active = tags.includes(jt.key);
                    return (
                      <button
                        key={jt.key}
                        type="button"
                        onClick={() => {
                          const next = active
                            ? tags.filter((k) => k !== jt.key)
                            : [...tags.filter((k) => k.toLowerCase() !== "all"), jt.key];
                          saveJobTypes(p, next);
                        }}
                        className={cls(
                          "rounded-full px-2 py-0.5 border transition-colors",
                          active
                            ? "border-accent bg-accent text-white"
                            : "border-line text-ink-2 hover:bg-surface-2"
                        )}
                        style={{ fontSize: 11 }}
                        data-testid={`master-phase-jt-${jt.key}-${p.id}`}
                      >
                        {jt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Input value={adding} onChange={setAdding} placeholder="e.g. Mechanical & Electrical" />
        <PrimaryButton icon={Plus} onClick={add}>Add phase</PrimaryButton>
      </div>
    </Card>
  );
}

// Small local cls helper (re-declared here to keep the tab self-contained).
const cls = (...xs) => xs.filter(Boolean).join(" ");

function PlaceholderCard({ icon: Icon, title, phase, hint }) {
  return (
    <Card className="p-7 flex items-center gap-5">
      <div className="w-11 h-11 rounded-full bg-surface-2 text-ink-3 flex items-center justify-center">
        <Icon size={18} />
      </div>
      <div className="flex-1">
        <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>{title}</h3>
        <p className="text-ink-3 mt-0.5" style={{ fontSize: 12 }}>{hint}</p>
      </div>
      <span className="col-head" style={{ background: "#F0EEE6", padding: "4px 8px", borderRadius: 4 }}>{phase}</span>
    </Card>
  );
}

/**
 * LT-Estimate-UX-01 Section 8 — Benchmarks & Defaults tab.
 *
 * One page for the app's judgement calls (per Chris's directive):
 *   1. £/m² base rate per job type   → Summary Card 7 target row
 *   2. Prelims reference band        → Summary Card 6 band flag
 *   3. Programme hours/day & crew    → Summary Card 8 rough programme
 *   4. Insights sample-size gates    → what n triggers a win-rate %
 *
 * Persists to the same `benchmark_config` settings doc consumed by the
 * Summary and Insights endpoints, so a save here immediately affects
 * every downstream figure on the next fetch — no service restart.
 */
function BenchmarksTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // LT-15 — £/m² rates key against the library. Read the full list
  // (including archived) so an old rate on an archived type still
  // shows its label + value.
  const { types: allJobTypes, activeTypes: activeJobTypes } = useJobTypeLibrary();

  useEffect(() => {
    api.get("/settings/benchmark-config")
      .then((r) => setCfg({
        base_rate_per_m2_by_job_type: r.data?.base_rate_per_m2_by_job_type || {},
        programme_hours_per_day: r.data?.programme_hours_per_day ?? 8.0,
        programme_crew_size:     r.data?.programme_crew_size     ?? 2.0,
        prelims_pct_lower_bound: r.data?.prelims_pct_lower_bound ?? 5.0,
        prelims_pct_upper_bound: r.data?.prelims_pct_upper_bound ?? 20.0,
        insights_min_sample_win_rate:     r.data?.insights_min_sample_win_rate     ?? 10,
        insights_min_sample_per_band:     r.data?.insights_min_sample_per_band     ?? 3,
        insights_min_sample_band_table:   r.data?.insights_min_sample_band_table   ?? 8,
        insights_min_sample_per_job_type: r.data?.insights_min_sample_per_job_type ?? 3,
        // Day Rate Check constants (Adjudication tab). Migrated in from
        // the retired /settings/tender-checks-config page.
        day_rate_hourly_rate:     r.data?.day_rate_hourly_rate     ?? 31.0,
        day_rate_hours_per_day:   r.data?.day_rate_hours_per_day   ?? 8.0,
        day_rate_avg_men_per_day: r.data?.day_rate_avg_men_per_day ?? 2.5,
      }))
      .catch(() => toast.error("Failed to load benchmarks"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg) return;
    // Sanity: lower ≤ upper on prelims band; positive thresholds.
    if (cfg.prelims_pct_lower_bound > cfg.prelims_pct_upper_bound) {
      toast.error("Prelims lower bound cannot exceed upper bound");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings/benchmark-config", cfg);
      toast.success("Benchmarks saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setJobRate = (key, val) => {
    const n = Number(val);
    setCfg((s) => {
      const map = { ...(s.base_rate_per_m2_by_job_type || {}) };
      if (!Number.isFinite(n) || n <= 0) delete map[key];
      else map[key] = n;
      return { ...s, base_rate_per_m2_by_job_type: map };
    });
  };

  if (loading || !cfg) return <div className="text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;

  return (
    <div className="flex flex-col gap-6" data-testid="benchmarks-tab">
      {/* --- 1) £/m² per job type ---------------------------------- */}
      <Card className="p-5">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
          £/m² base rate by job type
        </h3>
        <p className="text-ink-3 mb-4 flex items-start gap-1.5" style={{ fontSize: 12 }}>
          <Info size={12} className="mt-0.5 flex-shrink-0 text-ink-4" />
          <span>
            Shown as the "Target" row on the Summary tab's £/m² card. Leave empty
            (or set to zero) to hide the target for that job type — the historical
            average keeps working regardless once ≥3 closed tenders exist.
          </span>
        </p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 max-w-3xl">
          {activeJobTypes.map((jt) => (
            <Field key={jt.key} label={jt.label} data-testid={`bench-rate-${jt.key}`}>
              <NumberInput
                value={cfg.base_rate_per_m2_by_job_type?.[jt.key] ?? ""}
                step="1"
                onChange={(v) => setJobRate(jt.key, v)}
                prefix="£"
                suffix="/ m²"
              />
            </Field>
          ))}
          {activeJobTypes.length === 0 && (
            <div className="text-ink-3 italic col-span-2" style={{ fontSize: 12 }}>
              No active job types — add one under Settings → Job Types first.
            </div>
          )}
        </div>
      </Card>

      {/* --- 2) Prelims band --------------------------------------- */}
      <Card className="p-5">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
          Prelims reference band
        </h3>
        <p className="text-ink-3 mb-4 flex items-start gap-1.5" style={{ fontSize: 12 }}>
          <Info size={12} className="mt-0.5 flex-shrink-0 text-ink-4" />
          <span>
            Prelims as a % of build cost outside this band get flagged
            "worth checking" on the Summary tab. Not a hard rule — the
            band is guidance, not a blocker.
          </span>
        </p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
          <Field label="Lower bound">
            <NumberInput
              value={cfg.prelims_pct_lower_bound}
              step="0.5"
              onChange={(v) => setCfg((s) => ({ ...s, prelims_pct_lower_bound: Number(v || 0) }))}
              suffix="%"
              data-testid="bench-prelims-lower"
            />
          </Field>
          <Field label="Upper bound">
            <NumberInput
              value={cfg.prelims_pct_upper_bound}
              step="0.5"
              onChange={(v) => setCfg((s) => ({ ...s, prelims_pct_upper_bound: Number(v || 0) }))}
              suffix="%"
              data-testid="bench-prelims-upper"
            />
          </Field>
        </div>
      </Card>

      {/* --- 3) Rough programme ------------------------------------ */}
      <Card className="p-5">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
          Rough programme assumptions
        </h3>
        <p className="text-ink-3 mb-4 flex items-start gap-1.5" style={{ fontSize: 12 }}>
          <Info size={12} className="mt-0.5 flex-shrink-0 text-ink-4" />
          <span>
            Used by the Summary tab's Rough Programme card. Duration ≈
            total labour hours ÷ (crew × hours/day) ÷ 5-day week. Purely
            indicative — not a real programme.
          </span>
        </p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
          <Field label="Hours per day">
            <NumberInput
              value={cfg.programme_hours_per_day}
              step="0.5"
              onChange={(v) => setCfg((s) => ({ ...s, programme_hours_per_day: Number(v || 0) }))}
              suffix="h"
              data-testid="bench-programme-hours"
            />
          </Field>
          <Field label="Average crew size" hint="Number of operatives per day">
            <NumberInput
              value={cfg.programme_crew_size}
              step="0.5"
              onChange={(v) => setCfg((s) => ({ ...s, programme_crew_size: Number(v || 0) }))}
              data-testid="bench-programme-crew"
            />
          </Field>
        </div>
      </Card>

      {/* --- 4) Day Rate Check (Adjudication) ---------------------- */}
      <Card className="p-5">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
          Day Rate Check (Adjudication)
        </h3>
        <p className="text-ink-3 mb-4 flex items-start gap-1.5" style={{ fontSize: 12 }}>
          <Info size={12} className="mt-0.5 flex-shrink-0 text-ink-4" />
          <span>
            Powers the "Expected day rate" card on the Adjudication tab.
            Formula: <span className="font-mono">hourly × hours × men = £/day</span>.
            Adjudication compares this against each tender's actual
            derived day rate (labour cost ÷ duration).
          </span>
        </p>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3 max-w-3xl">
          <Field label="Hourly rate" hint="Charge-out rate for a general operative">
            <NumberInput
              value={cfg.day_rate_hourly_rate}
              step="0.5"
              onChange={(v) => setCfg((s) => ({ ...s, day_rate_hourly_rate: Number(v || 0) }))}
              prefix="£"
              suffix="/h"
              data-testid="bench-dayrate-hourly"
            />
          </Field>
          <Field label="Hours per day">
            <NumberInput
              value={cfg.day_rate_hours_per_day}
              step="0.5"
              onChange={(v) => setCfg((s) => ({ ...s, day_rate_hours_per_day: Number(v || 0) }))}
              suffix="h"
              data-testid="bench-dayrate-hours"
            />
          </Field>
          <Field label="Average men per day" hint="Typical crew size on site">
            <NumberInput
              value={cfg.day_rate_avg_men_per_day}
              step="0.5"
              onChange={(v) => setCfg((s) => ({ ...s, day_rate_avg_men_per_day: Number(v || 0) }))}
              data-testid="bench-dayrate-men"
            />
          </Field>
        </div>
        {(() => {
          const dr = (cfg.day_rate_hourly_rate || 0) * (cfg.day_rate_hours_per_day || 0) * (cfg.day_rate_avg_men_per_day || 0);
          return (
            <div className="mt-3 text-ink-3 font-mono" style={{ fontSize: 12 }} data-testid="bench-dayrate-preview">
              Preview: £{(cfg.day_rate_hourly_rate || 0).toFixed(2)} × {cfg.day_rate_hours_per_day}h × {cfg.day_rate_avg_men_per_day} = <span className="text-ink" style={{ fontWeight: 600 }}>£{dr.toFixed(2)} / day</span>
            </div>
          );
        })()}
      </Card>

      {/* --- 5) Insights thresholds -------------------------------- */}
      <Card className="p-5">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
          Insights sample-size thresholds
        </h3>
        <p className="text-ink-3 mb-4 flex items-start gap-1.5" style={{ fontSize: 12 }}>
          <Info size={12} className="mt-0.5 flex-shrink-0 text-ink-4" />
          <span>
            The Insights page refuses to quote a win rate below these
            counts — small samples produce numbers that read as
            insight but are noise. Raise them to be more conservative;
            lower them (advisedly) to see the page populate sooner.
          </span>
        </p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
          <Field label="Overall win-rate" hint="Headline % appears only when total closed ≥ this">
            <NumberInput
              value={cfg.insights_min_sample_win_rate}
              step="1"
              onChange={(v) => setCfg((s) => ({ ...s, insights_min_sample_win_rate: Math.max(1, Math.floor(Number(v || 0))) }))}
              suffix="min n"
              data-testid="bench-insights-overall"
            />
          </Field>
          <Field label="Per O&P band" hint="A band shows % only when its own n ≥ this">
            <NumberInput
              value={cfg.insights_min_sample_per_band}
              step="1"
              onChange={(v) => setCfg((s) => ({ ...s, insights_min_sample_per_band: Math.max(1, Math.floor(Number(v || 0))) }))}
              suffix="min n"
              data-testid="bench-insights-per-band"
            />
          </Field>
          <Field label="Whole band table" hint="Table dims to 'need more data' below this">
            <NumberInput
              value={cfg.insights_min_sample_band_table}
              step="1"
              onChange={(v) => setCfg((s) => ({ ...s, insights_min_sample_band_table: Math.max(1, Math.floor(Number(v || 0))) }))}
              suffix="min n"
              data-testid="bench-insights-band-table"
            />
          </Field>
          <Field label="Per job type" hint="Rows hidden entirely below this">
            <NumberInput
              value={cfg.insights_min_sample_per_job_type}
              step="1"
              onChange={(v) => setCfg((s) => ({ ...s, insights_min_sample_per_job_type: Math.max(1, Math.floor(Number(v || 0))) }))}
              suffix="min n"
              data-testid="bench-insights-per-jt"
            />
          </Field>
        </div>
      </Card>

      {/* --- Save bar ---------------------------------------------- */}
      <div className="sticky bottom-0 bg-surface-2/95 backdrop-blur border-t border-line px-4 py-3 flex items-center justify-end gap-2 -mx-4">
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : Save} data-testid="bench-save">
          {saving ? "Saving…" : "Save benchmarks"}
        </PrimaryButton>
      </div>
    </div>
  );
}

function ConnectorsTab() {
  return (
    <div className="flex flex-col gap-3" data-testid="connectors-tab">
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>Bluebeam Revu — one-click open</h3>
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 12 }}>
              Set up this desktop PC once so drawings open straight in Bluebeam Revu from the browser. You only do this per machine — after that, "Open in Bluebeam" on any drawing just works.
            </p>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ background: "#EEF2FF", color: "#3730A3", fontSize: 11, fontWeight: 600 }}>
            <Cloud size={12} /> Desktop
          </span>
        </div>
        <div className="mt-4 pt-4 border-t border-line">
          <BluebeamSetupSteps withDownload downloadTestid="connectors-bluebeam-download" />
        </div>
      </Card>
    </div>
  );
}

function IntegrationsTab() {
  const { user } = useAuth();
  return (
    <div className="flex flex-col gap-3">
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>Google sign-in (Emergent managed)</h3>
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 12 }}>
              Authentication is provided via Emergent’s managed Google OAuth. Currently signed in as <span className="text-ink-2 font-medium">{user?.email}</span>.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ background: "#E8EFE9", color: "#2F4F3E", fontSize: 11, fontWeight: 600 }}>
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />Connected
          </span>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>Microsoft OneDrive</h3>
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 12 }}>
              Tender folder paths are reserved on creation but real folders are not yet provisioned. Full Microsoft Graph integration is deferred.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ background: "#FEF3E2", color: "#B45309", fontSize: 11, fontWeight: 600 }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#D97706" }} />Stub
          </span>
        </div>
      </Card>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab") || "general";
  const tab = LEGACY_TAB_IDS[rawTab] || rawTab;
  const setTab = (id) => setParams(id === "general" ? {} : { tab: id }, { replace: true });
  const visibleTabs = TABS.filter((t) => !t.adminOnly || user?.role === "admin");
  const libraryActive = LIBRARY_TABS.find((l) => l.id === tab);
  return (
    <div className="app-container py-8">
      <PageHeader title="Settings" subtitle="Workspace-wide configuration." />

      <div className="flex items-center gap-1 border-b border-line mb-6">
        {visibleTabs.map((t) => t.group ? (
          <LibraryTabButton key={t.id} label={t.label} items={t.group} active={libraryActive} onPick={setTab} />
        ) : (
          <button
            key={t.id} onClick={() => setTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className={[
              "relative py-3 px-3 transition-colors",
              tab === t.id ? "text-ink" : "text-ink-3 hover:text-ink-2"
            ].join(" ")}
            style={{ fontSize: 13, fontWeight: tab === t.id ? 600 : 500 }}
          >
            {t.label}
            {tab === t.id && (<span className="absolute left-3 right-3 -bottom-px h-[2px]" style={{ background: "#2F4F3E" }} />)}
          </button>
        ))}
      </div>

      {tab === "general"     && <GeneralTab />}
      {tab === "users"       && <UsersTab />}
      {tab === "library_materials" && (
        <div data-testid="settings-library-materials">
          <LibraryCrossLink to="/materials?tab=settings" label="Libraries → Materials → Settings" />
          <MaterialsSettingsTab />
        </div>
      )}
      {tab === "library_labour" && (
        <div data-testid="settings-library-labour">
          <LibraryCrossLink to="/labour-rates?tab=settings" label="Libraries → Labour Rates → Settings" />
          <LabourRatesBulkTab />
        </div>
      )}
      {tab === "job_types"   && <JobTypesTab />}
      {tab === "phases"      && <PhasesTab />}
      {tab === "milestones"  && <MilestonesTab />}
      {tab === "working_calendar" && <WorkingCalendarTab />}
      {tab === "programme"   && <ProgrammeDefaultsTab />}
      {tab === "export_defaults" && <ExportDefaultsTab />}
      {tab === "provsum_defaults" && <ProvsumDefaultsTab />}
      {tab === "insurance"   && <InsuranceTab />}
      {tab === "dictionary"  && <DictionaryTabRedirect />}
      {tab === "benchmarks"  && <BenchmarksTab />}
      {tab === "output"      && <DocumentCopyTab />}
      {tab === "ai"          && <PlaceholderCard icon={Sparkles} title="AI prompts" phase="LT-23" hint="Master prompts and AI feature toggles arrive in Wave 1 of the AI rollout." />}
      {tab === "integrations" && <IntegrationsTab />}
      {tab === "connectors"  && <ConnectorsTab />}
    </div>
  );
}

// Mirrors DictionaryTabRedirect — keeps deep-linkable URL for the day-rate
// constants page while providing a discoverable Settings tab entry-point.
function TenderChecksRedirect() {
  return (
    <Card className="p-5 text-ink-2" data-testid="tender-checks-tab-redirect">
      <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>Tender Checks</h3>
      <p className="text-ink-3 mb-3" style={{ fontSize: 12.5 }}>
        Day-rate sanity constants used on the Adjudication tab (hourly rate, hours per day, average crew size).
      </p>
      <a
        href="/settings/tender-checks-config"
        data-testid="tender-checks-tab-link"
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-accent text-white hover:bg-accent-deep"
        style={{ fontSize: 12, fontWeight: 500 }}
      >
        Open tender checks config
      </a>
    </Card>
  );
}

// Tiny shim — the dictionary tab in Settings now sends users to the
// dedicated /settings/dictionary admin page (so the table + dialog have
// their own URL, breadcrumbs, deep-linkable rows).
function DictionaryTabRedirect() {
  return (
    <Card className="p-5 text-ink-2" data-testid="dictionary-tab-redirect">
      <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>Custom Dictionary</h3>
      <p className="text-ink-3 mb-3" style={{ fontSize: 12.5 }}>
        Construction-specific terms to teach the spell-checker.
      </p>
      <a
        href="/settings/dictionary"
        data-testid="dictionary-tab-link"
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-accent text-white hover:bg-accent-deep"
        style={{ fontSize: 12, fontWeight: 500 }}
      >
        Open dictionary admin
      </a>
    </Card>
  );
}
